import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '../../Shared/Components/ui/Button';
import { Input } from '../../Shared/Components/ui/Input';
import { VStack } from '../../Shared/Components/ui/Stack';
import { useLoginMutation } from '../AuthApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateSessionId, isSafeRedirectPath } from '../../Shared/lib/utils';
import { toast } from 'react-toastify';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useAuth } from '../../Shared/Hooks/useAuth';
import { useInstance } from '../../Shared/Hooks/useInstance';
import AuthShell from '../Shared/AuthShell';
import AuthCard from '../Shared/AuthCard';
import AuthFooter from '../Shared/AuthFooter';

interface LoginFormData {
  username: string;
  password: string;
}

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();
  const [login, { isLoading }] = useLoginMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useInstance();
  const { user } = useAuth();
  const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;

  useEffect(() => {
    if (user) {
      navigate(
        isSafeRedirectPath(from?.pathname) ? from.pathname + (from.search ?? '') : '/dashboard/students'
      );
    }
  }, [navigate, user, from]);

  const onSubmit: SubmitHandler<LoginFormData> = async (data, event) => {
    event?.preventDefault();
    setErrorMessage(null);
    const sessionId = generateSessionId();
    const dataWithSessionId = { ...data, sessionId };
    try {
      const response = await login(dataWithSessionId).unwrap();
      navigate('/otp/select', {
        state: {
          username: data.username,
          password: data.password,
          sessionId,
          contacts: response.contacts,
          from,
        },
      });
    } catch (error: any) {
      if (error) {
        setErrorMessage('Invalid username or password');
      }

      const isNetworkError =
        (error as FetchBaseQueryError).status === 'FETCH_ERROR';
      const errorMessage = isNetworkError
        ? 'Network error. Please check your connection and try again.'
        : 'Login failed. Please check your credentials and try again.';

      toast.error(errorMessage);
    }
  };

  return (
    <AuthShell
      side={{
        title: (
          <>
            Elevate Your <br />
            <span className="text-teal-accent">School Experience</span>
          </>
        ),
        text: 'The future of school efficiency starts with Skuulbe.',
      }}
    >
      <AuthCard logo={instance?.logo}>
          <div className="text-center mb-10 pt-6">
            <h1 className="text-2xl tablet:text-3xl bricolage-grotesque-extrabold text-[#111827] mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-secondary text-sm tablet:text-base manrope-medium">
              Log in to manage your school efficiently
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={4}>
              <Input
                label="Username"
                className="h-12"
                type="text"
                placeholder="Enter your username"
                {...register('username', {
                  required: 'Username is required',
                })}
                error={errors?.username}
              />

              <Input
                label="Password"
                className="h-12"
                type="password"
                placeholder="••••••••"
                {...register('password', { required: 'Password is required' })}
                error={errors?.password}
              />
            </VStack>

            {errorMessage && (
              <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm animate-shake">
                {errorMessage}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full h-14 text-lg"
              isLoading={isLoading}
            >
              Sign In
            </Button>

            <AuthFooter displayName={instance?.displayName} />
          </form>
      </AuthCard>
    </AuthShell>
  );
};

export default Login;
